import { Paperclip, X } from "lucide-react";
import { cn } from "@/lib/utils";

/** Props for the AttachmentPill component. */
interface AttachmentPillProps {
  fileName: string;
  onClick: () => void;
  onRemove?: () => void;
  disabled?: boolean;
}

/**
 * Small file pill: clicking the name opens the file, and the optional X removes it.
 * The pill is a div so the name and the remove control can both be real buttons.
 */
export function AttachmentPill({
  fileName,
  onClick,
  onRemove,
  disabled = false,
}: AttachmentPillProps) {
  return (
    <div
      title={fileName}
      className={cn(
        "inline-flex max-w-full items-center gap-1.5 rounded-md border bg-card px-2 py-1 text-xs text-foreground/90 transition-colors hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive",
        disabled && "pointer-events-none opacity-50",
      )}
    >
      <button
        type="button"
        className="inline-flex min-w-0 items-center gap-1.5 rounded-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        onClick={onClick}
        disabled={disabled}
      >
        <Paperclip className="h-3 w-3 shrink-0 text-muted-foreground" />
        <span className="truncate max-w-[200px]">{fileName}</span>
      </button>
      {onRemove && (
        <button
          type="button"
          className="shrink-0 rounded-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          onClick={onRemove}
          disabled={disabled}
          aria-label={`Remove ${fileName}`}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}
